import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {ShippingCarrierTypeDto, ShippingClientDto} from "../../api/fms";
import {validationStore} from "../ValidationStore";
import {equals} from "../../utils";
import {FmsShippingFormData, FmsShippingFormValidation} from "./FmsShippingClientCommons";

const systemApi = api.fms.api

class FmsShippingClientDetailStore {
    get loaded(): ShippingClientDto | undefined {
        return this._loaded;
    }

    set loaded(value: ShippingClientDto | undefined) {
        this._loaded = value;
    }

    get id(): string {
        return this._id;
    }

    private set id(value: string) {
        this._id = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get label(): string {
        return this._label;
    }

    set label(value: string) {
        this._label = value;
    }

    get clientNumber(): string {
        return this._clientNumber;
    }

    set clientNumber(value: string) {
        this._clientNumber = value;
    }

    get carrier(): ShippingCarrierTypeDto {
        return this._carrier;
    }

    set carrier(value: ShippingCarrierTypeDto) {
        this._carrier = value;
    }

    get saving(): boolean {
        return this._saving;
    }

    private set saving(value: boolean) {
        this._saving = value;
    }

    get formValidation(): FmsShippingFormValidation {
        return {
            carrier: validationStore.getFor('carrier'),
            clientNumber: validationStore.getFor('clientNumber'),
            label: validationStore.getFor('label'),
        }
    }

    get formData(): FmsShippingFormData {
        const {
            carrier,
            clientNumber,
            label
        } = this
        return {
            label,
            carrier,
            clientNumber
        }
    }

    set formData(value: FmsShippingFormData) {
        const {
            carrier,
            clientNumber,
            label
        } = value
        this.carrier = carrier
        this.clientNumber = clientNumber
        this.label = label
    }

    get dirty(): boolean {
        const current = this.buildDto()
        return !equals(this.loaded, current)
    }

    private _carrier: ShippingCarrierTypeDto = ShippingCarrierTypeDto.DHL
    private _clientNumber: string = ''
    private _label: string = ''

    private _id: string = ''
    private _valid: boolean = false
    private _saving: boolean = false
    private _loaded: ShippingClientDto | undefined

    constructor() {
        makeAutoObservable(this)
        this.reset()
    }

    async load(id: string) {
        this.id = id
        if (!id) {
            return null
        }
        const response = await catchApi(
            async () => await systemApi.getShippingClient(id)
        )
        const data = response?.data
        this.loaded = data
        this.reset()
        return data
    }

    reset() {
        const data = this.loaded?.data
        if (!data) {
            return
        }
        this.carrier = data.carrier
        this.clientNumber = data.clientNumber
        this.label = data.label
    }

    async save(): Promise<any> {
        this.saving = true
        const dto = this.buildDto()
        const response = await catchApi(
            async () => await systemApi.updateShippingClient(this.id, dto)
        )
        const result = response?.data
        this.saving = false
        return result
    }

    async validate() {
        this.valid = false
        const dto = this.buildDto()
        if (!dto) {
            return
        }
        await validationStore.validate(systemApi.validateShippingClient, dto.data)
        this.valid = Boolean(validationStore.valid)
    }

    buildDto(): ShippingClientDto {
        const {
            loaded,
            carrier,
            clientNumber,
            label
        } = this
        return {
            audit: loaded?.audit!,
            data: {
                label,
                carrier,
                clientNumber
            }
        }
    }

}

const fmsShippingClientDetailStore = new FmsShippingClientDetailStore()

export {
    fmsShippingClientDetailStore
}
